import { InfoCircleOutlined } from '@ant-design/icons';
import CaretRightOutlined from '@ant-design/icons/CaretRightOutlined';
import CheckOutlined from '@ant-design/icons/CheckOutlined';
import CloseOutlined from '@ant-design/icons/CloseOutlined';
import WarningOutlined from '@ant-design/icons/WarningOutlined';
import App from 'antd/lib/app';
import Button from 'antd/lib/button';
import Col from 'antd/lib/col';
import Collapse from 'antd/lib/collapse';
import DatePicker from 'antd/lib/date-picker';
import Divider from 'antd/lib/divider';
import Form from 'antd/lib/form';
import FormItem from 'antd/lib/form/FormItem';
import Input from 'antd/lib/input';
import InputNumber from 'antd/lib/input-number';
import Modal from 'antd/lib/modal';
import Radio from 'antd/lib/radio';
import Row from 'antd/lib/row';
import Select from 'antd/lib/select';
import Space from 'antd/lib/space';
import Switch from 'antd/lib/switch';
import Tooltip from 'antd/lib/tooltip';
import Typography from 'antd/lib/typography';
import { theme } from 'antd/lib';
import { useCallback, useMemo, useState } from 'react';
import { ProgramSelector } from '../../../components/selectors/ProgramSelector/ProgramSelector';
import { formatToMinutesAndHours, getComputerFormattedTime } from '../../../domain/date/date';
import { createEventBatchByProgram } from '../../../domain/event';

import { IEvent, SIGNAL_LEVEL_OPTIONS, SliceType, SliceTypeLabel } from '../../../domain/event/interface';
import { FORM_ACTION, toBatchRequest, ViewEvent } from '../eventInterface';
import { isPastDate } from '../utils';
import { EventConfirmationModal } from './EventConfirmationModal';
import { CalendarEventPreview } from './CalendarEventPreview';
import { useAuth } from '../../../domain/auth/useAuth';
import { useResourceListQuery } from '../../../domain/event/queries';
import { ResourceCollapsedList } from './ResourceCollapsedList/ResourceCollapsedList';

export const EventByProgramFormModal = ({ event, onClose }: { event: Partial<IEvent>; onClose: Function }) => {
    const { token: themeColors } = theme.useToken();

    const { notification } = App.useApp();
    const [loading, setLoading] = useState<boolean>(false);
    const viewEvent = useMemo(() => new ViewEvent(event), [event]);
    const [formAction, setFormAction] = useState<FORM_ACTION | null>(null);
    const [state, setState] = useState({} as any);
    const [form] = Form.useForm();
    const auth = useAuth();
    const [programId, setProgramId] = useState(null);
    const { data, isLoading: isLoadingSites, isError, error } = useResourceListQuery({ programId: programId }, { enabled: !!programId });
    const sites = useMemo(() => data || [], [data]);

    if (isError) {
        notification.error({ key: 'fetch-sites-error', message: error.message || 'Cannot fetch sites!' });
    }

    const isCustomer = !!auth?.user?.isCustomer();

    const isNoEndTimeEvent = Form.useWatch('no_end_time', form);
    const isTestEvent = Form.useWatch('event_test', form);
    const isEmergencyEvent = Form.useWatch('event_emergency_generator_allowed', form);
    const isVoluntaryEvent = Form.useWatch('event_voluntary', form);

    const preEventDuration = Form.useWatch('event_pre_duration_hours', form);
    const eventDuration = Form.useWatch('event_duration_hours', form);
    const postEventDuration = Form.useWatch('event_post_duration_hours', form);
    const secondaryPreEventDuration = Form.useWatch('event_secondary_duration_hours', form);
    const eventDatetimeStart = Form.useWatch('event_datetime_start_local', form);

    function handleNoEndTimeChange(value: boolean) {
        if (value) {
            // we need to clear previous selected values for post and secondary durations and for event slice
            form.setFieldsValue({
                event_post_duration_hours: 0,
                event_secondary_duration_hours: 0,
                event_slice: { type: SliceType.NONE, amount: 0 },
            });

            viewEvent.event_duration_hours = 12;
        } else {
            viewEvent.event_duration_hours = 1;
        }
    }

    const handleProgramSelect = (value: number | string, option: any) => {
        setState((prevState: any) => ({
            ...prevState,
            program: { program_name: option.label, program_id: option.value },
        }));

        setProgramId(option.value);
    };

    const handleProgramClear = () => {
        setProgramId(null);
    };

    const createEventWithConfirmation = () => setFormAction(FORM_ACTION.CREATE);

    const onEventFormFinish = useCallback(() => createEventWithConfirmation(), []);

    const createBatchAction = async () => {
        setLoading(true);

        try {
            const preparedEvent = await form.validateFields();
            const { event } = toBatchRequest(preparedEvent);

            await createEventBatchByProgram(event, [state?.program?.program_id]);

            notification.info({ key: 'event-save-info', message: 'Events saved' });
            onClose(event);
        } catch (err: any) {
            notification.error({ key: 'event-save-error', message: err.message || 'Cannot save events!' });
        }

        setLoading(false);
    };

    const closeEventConfirmationModal = () => setFormAction(null);

    const closeDialog = () => onClose();

    return (
        <Modal
            open
            destroyOnHidden
            title="New event by program"
            onCancel={closeDialog}
            footer={[
                <Space wrap>
                    <Button key="event-form-cancel" onClick={closeDialog}>
                        Close
                    </Button>

                    <Button key="event-form-submit" type="primary" loading={loading} onClick={form.submit}>
                        Save
                    </Button>
                </Space>,
            ]}
            className="event-modal"
        >
            <div className="event-form-container content-background">
                <Form form={form} name="event-form" preserve={false} layout="vertical" onFinish={onEventFormFinish} initialValues={viewEvent}>
                    <Col xs={24}>
                        {!!programId && (
                            <Form.Item
                                name="resources"
                                label={<Typography.Text strong>Resources</Typography.Text>}
                                hasFeedback
                                wrapperCol={{ span: 24 }}
                            >
                                <ResourceCollapsedList resources={sites} eventDatetime={eventDatetimeStart} loading={isLoadingSites} showTimezone />
                            </Form.Item>
                        )}
                    </Col>

                    <Row>
                        <Col xs={24}>
                            <Form.Item
                                required
                                label={<Typography.Text strong>Program</Typography.Text>}
                                name="program_id"
                                rules={[{ required: true, message: 'Please select program!' }]}
                            >
                                <ProgramSelector
                                    allowClear
                                    handleProgram={() => null}
                                    selectedProgram={state?.program?.program_id || null}
                                    excludeUtilityCustomerPrograms={isCustomer}
                                    onSelect={handleProgramSelect}
                                    onClear={handleProgramClear}
                                />
                            </Form.Item>

                            <Form.Item name="event_id" hidden>
                                <Input />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row>
                        <Col xs={24}>
                            <Form.Item label={<Typography.Text strong>Pre Event duration</Typography.Text>}>
                                <Space>
                                    <Form.Item name="event_pre_duration_hours" noStyle>
                                        <InputNumber min={0} max={4} step={0.25} size="large" />
                                    </Form.Item>
                                    <Typography.Text>hours</Typography.Text>
                                </Space>
                            </Form.Item>

                            <Form.Item style={{ marginBottom: 0 }}>
                                <Space>
                                    <Form.Item
                                        name="event_datetime_start_local"
                                        required
                                        rules={[{ required: true, message: 'Please select date!' }]}
                                        label={
                                            <>
                                                <Typography.Text strong>Event start&nbsp;</Typography.Text>
                                                <Tooltip title="The event will be dispatched to start at each site's local timezone">
                                                    <InfoCircleOutlined style={{ color: themeColors.colorPrimary }} />
                                                </Tooltip>
                                            </>
                                        }
                                    >
                                        <DatePicker format="YYYY-MM-DD HH:mm" showTime={{ format: 'HH:mm' }} size="large" />
                                    </Form.Item>
                                    <Form.Item dependencies={['event_datetime_start_local']} style={{ marginBottom: 0 }}>
                                        {({ getFieldValue }) => {
                                            const selectedDate = getFieldValue('event_datetime_start_local');

                                            return (
                                                selectedDate &&
                                                isPastDate(getFieldValue('event_datetime_start_local')) && (
                                                    <Typography.Text type="secondary">
                                                        <WarningOutlined style={{ color: '#faad14' }} /> Event started in the past.
                                                    </Typography.Text>
                                                )
                                            );
                                        }}
                                    </Form.Item>
                                </Space>
                            </Form.Item>
                        </Col>
                    </Row>
                    <Row>
                        <Col xs={12}>
                            <Form.Item
                                name="no_end_time"
                                label={
                                    <>
                                        <Typography.Text strong>No End Time&nbsp;</Typography.Text>
                                        <Tooltip title="The event will be dispatched for 12h">
                                            <InfoCircleOutlined style={{ color: themeColors.colorPrimary }} />
                                        </Tooltip>
                                    </>
                                }
                                valuePropName="checked"
                                style={{ paddingBottom: '9px' }}
                            >
                                <Switch checkedChildren={<CheckOutlined />} unCheckedChildren={<CloseOutlined />} onChange={handleNoEndTimeChange} />
                            </Form.Item>
                        </Col>
                        {!isNoEndTimeEvent ? (
                            <Col xs={12}>
                                <Form.Item label={<Typography.Text strong>Event duration</Typography.Text>}>
                                    <Space>
                                        <Form.Item name="event_duration_hours" noStyle>
                                            <InputNumber data-cy="event-duration" min={0} max={12} step={0.25} size="large" />
                                        </Form.Item>
                                        <Typography.Text>hours</Typography.Text>
                                    </Space>
                                </Form.Item>
                            </Col>
                        ) : (
                            <Form.Item name="event_duration_hours" hidden>
                                <InputNumber />
                            </Form.Item>
                        )}
                    </Row>

                    <Collapse
                        key={isNoEndTimeEvent ? 'disabled' : 'enabled'}
                        accordion
                        size="small"
                        collapsible={isNoEndTimeEvent && 'disabled'}
                        expandIcon={({ isActive }) => <CaretRightOutlined rotate={isActive ? 90 : 0} />}
                    >
                        <Collapse.Panel key="1" header="Slice">
                            <FormItem
                                wrapperCol={{ span: 24 }}
                                label={<Typography.Text strong>Slice Type</Typography.Text>}
                                name={['event_slice', 'type']}
                            >
                                <Radio.Group>
                                    {Object.entries(SliceTypeLabel).map(([value, label]) => (
                                        <Radio key={value} value={value} disabled={value === SliceType.KW}>
                                            {label}
                                        </Radio>
                                    ))}
                                </Radio.Group>
                            </FormItem>
                            <FormItem label={<Typography.Text strong>Slice Amount</Typography.Text>} name={['event_slice', 'amount']}>
                                <InputNumber min={0} size="large" />
                            </FormItem>
                        </Collapse.Panel>
                    </Collapse>

                    <Divider plain>Advanced</Divider>

                    <Row>
                        <Col xs={24}>
                            <Form.Item label={<Typography.Text strong>OADR Signal Level</Typography.Text>} name="event_signal">
                                <Select
                                    placeholder="Please select signal level"
                                    options={SIGNAL_LEVEL_OPTIONS}
                                    style={{ width: '175px' }}
                                    size="large"
                                />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row>
                        <Col xs={12}>
                            <Form.Item label={<Typography.Text strong>Post Event</Typography.Text>}>
                                <Space>
                                    <Form.Item noStyle name="event_post_duration_hours">
                                        <InputNumber min={0} max={4} step={0.25} size="large" disabled={isNoEndTimeEvent} />
                                    </Form.Item>
                                    <Typography.Text>hours</Typography.Text>
                                </Space>
                            </Form.Item>
                        </Col>

                        <Col xs={12}>
                            <Form.Item label={<Typography.Text strong>Secondary Pre Event</Typography.Text>}>
                                <Space>
                                    <Form.Item name="event_secondary_duration_hours" noStyle>
                                        <InputNumber min={0} max={4} step={0.25} size="large" disabled={isNoEndTimeEvent} />
                                    </Form.Item>
                                    <Typography.Text>hours</Typography.Text>
                                </Space>
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row>
                        <Col xs={12}>
                            <Form.Item
                                name="event_test"
                                label={
                                    <Typography.Text strong>
                                        Communications Test <br /> (no load drop)
                                    </Typography.Text>
                                }
                                valuePropName="checked"
                            >
                                <Switch checkedChildren={<CheckOutlined />} unCheckedChildren={<CloseOutlined />} />
                            </Form.Item>
                        </Col>

                        <Col xs={12}>
                            <Form.Item
                                name="event_emergency_generator_allowed"
                                label={<Typography.Text strong>Emergency Generators Allowed</Typography.Text>}
                                valuePropName="checked"
                            >
                                <Switch checkedChildren={<CheckOutlined />} unCheckedChildren={<CloseOutlined />} />
                            </Form.Item>
                            <Form.Item
                                name="event_voluntary"
                                label={<Typography.Text strong>Voluntary Event</Typography.Text>}
                                valuePropName="checked"
                            >
                                <Switch checkedChildren={<CheckOutlined />} unCheckedChildren={<CloseOutlined />} />
                            </Form.Item>
                        </Col>
                    </Row>

                    <CalendarEventPreview
                        isTest={isTestEvent}
                        isEmergency={isEmergencyEvent}
                        isVoluntary={isVoluntaryEvent}
                        isNoEndTime={isNoEndTimeEvent}
                        preEventDurationHours={preEventDuration}
                        eventDurationHours={eventDuration}
                        postEventDurationHours={postEventDuration}
                        secondaryPreEventDurationHours={secondaryPreEventDuration}
                    />
                </Form>

                {formAction && (
                    <EventConfirmationModal
                        action={formAction}
                        handleConfirm={createBatchAction}
                        handleClose={closeEventConfirmationModal}
                        modalContent={
                            <ConfirmationModalContent
                                programName={state.program.program_name}
                                event={{
                                    ...form.getFieldsValue(),
                                    ...([FORM_ACTION.CANCEL, FORM_ACTION.CANCEL_BATCH].includes(formAction) && {
                                        event_datetime_start_local: event.event_datetime_start,
                                    }),
                                }}
                            />
                        }
                    />
                )}
            </div>
        </Modal>
    );
};

interface IConfirmationModalContent {
    event: ViewEvent;
    programName: string;
}

const ConfirmationModalContent = ({ event, programName }: IConfirmationModalContent) => {
    const event_duration_in_minutes = event.event_duration_hours * 60;
    const event_pre_duration_in_minutes = event.event_pre_duration_hours * 60;

    return (
        <>
            <Typography>
                You are going to dispatch <strong>all sites</strong> connected to program: <strong>{programName}</strong>
            </Typography>
            <Typography>
                events start at site's local time:{' '}
                <strong>{getComputerFormattedTime(event.event_datetime_start_local!, 'dd/MMM/yyyy hh:mm a')}</strong>
            </Typography>
            {Boolean(event_pre_duration_in_minutes) && (
                <Typography>
                    events pre duration: <strong>{formatToMinutesAndHours(event_pre_duration_in_minutes)}</strong>.
                </Typography>
            )}
            <Typography>
                events duration: <strong>{!event.no_end_time ? formatToMinutesAndHours(event_duration_in_minutes) : 'unknown'}</strong>.
            </Typography>
        </>
    );
};
